import { useCallback, useState } from 'react';

import { useAtomValue } from 'jotai';

import { Unit } from '@core';
import { ActivitySwing } from '@data';
import { useUserSettingsWithDefaults } from '@hooks';
import { useTranslate } from '@i18n';
import {
    useDeleteSwing,
    useSwingAnalysis,
    useSwingThreadsByEntity,
    useThreadMessageUnreadNotifications,
    useUpdateUserSettings,
} from '@queries';
import { activityUnitAtom } from '@store';
import { ActivityListItem, ComparisonIcon, iconColors, Menu, MenuItem, StarIcon, TrashIcon } from '@ui';

import { getDisplayValue } from './getDisplayValue';
import { SwingDeleteConfirmationModal } from './SwingDeleteConfirmationModal';

interface Parameter {
    parameterName: string;
    parameterID: string;
    parameterUnit: Unit;
}
export type SwingActivityProps = {
    onFavorite: () => void;
    onDelete?: () => void;
    onSelectActive: (swingId: string) => void;

    activeSwingId: string | null;

    activeUserId?: string;
    hasUnreadNotification?: boolean;
};

const SWING_ACTIVITY_BUTTON_SIZE = 18;

export const SwingActivity = ({
    uuid,
    favorite,
    number,
    parameterID,
    parameterUnit,
    onFavorite,
    onDelete,
    onSelectActive,

    activeSwingId,

    activeUserId,
    activityId,
}: ActivitySwing & Parameter & SwingActivityProps) => {
    const activityUnit = useAtomValue(activityUnitAtom);
    const { data: swingAnalysis } = useSwingAnalysis(uuid);
    const { data: userSettings } = useUserSettingsWithDefaults(activeUserId);
    const { data: unreadNotifications } = useThreadMessageUnreadNotifications();
    const { data: numberOfThreadsInSwing } = useSwingThreadsByEntity(activityId, uuid);
    const { mutateAsync: updateUserSettings } = useUpdateUserSettings(activeUserId);

    const [isSwingDeleteModalOpen, setIsSwingDeleteModalOpen] = useState(false);
    const hasThread = !!numberOfThreadsInSwing?.length;
    const parameterValue = getDisplayValue(activityUnit, parameterID, parameterUnit, swingAnalysis);

    const { mutateAsync: deleteSwing, isPending: isDeletingSwing } = useDeleteSwing();
    const { comparisonSwingId } = userSettings;
    const onToggleComparison = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            return updateUserSettings({ comparisonSwingId: comparisonSwingId !== uuid ? uuid : null });
        },
        [comparisonSwingId, updateUserSettings, uuid],
    );

    const onFavoriteClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onFavorite();
        },
        [onFavorite],
    );

    const onDeleteClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setIsSwingDeleteModalOpen(true);
    }, []);

    const handleDeleteDismiss = useCallback(() => {
        setIsSwingDeleteModalOpen(false);
    }, []);

    const handleDeleteAccept = useCallback(async () => {
        await deleteSwing({ activityId, swingUuid: uuid });
        setIsSwingDeleteModalOpen(false);
        if (uuid === activeSwingId) {
            onDelete?.();
        }
    }, [deleteSwing, activityId, uuid, activeSwingId, onDelete]);

    const hasUnreadNotification = !!unreadNotifications?.swings?.[uuid]?.length;

    const t = useTranslate();

    return (
        <>
            <ActivityListItem
                id={uuid}
                key={uuid}
                number={number}
                onSelect={() => onSelectActive(uuid)}
                status={swingAnalysis?.fullAnalysis ? 'ready' : 'analyzing'}
                value={parameterValue}
                flags={{
                    isSelected: activeSwingId === uuid,
                    isComparison: comparisonSwingId === uuid,
                    isFavorite: favorite,
                    hasUnreadNotification: hasUnreadNotification,
                    hasThread: hasThread,
                }}
                menu={
                    <Menu
                        label={t('activity_navigation.menu_label')}
                        align="right"
                        buttonSize={SWING_ACTIVITY_BUTTON_SIZE}
                        buttonPadding={28 - SWING_ACTIVITY_BUTTON_SIZE}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        <MenuItem style={{ paddingInline: SWING_ACTIVITY_BUTTON_SIZE }} onClick={onFavoriteClick}>
                            <StarIcon size={SWING_ACTIVITY_BUTTON_SIZE} color={iconColors.favorite} />
                            <span>
                                {favorite
                                    ? t('activity_navigation.item_remove_as_favorite')
                                    : t('activity_navigation.item_mark_as_favorite')}
                            </span>
                        </MenuItem>
                        <MenuItem style={{ paddingInline: SWING_ACTIVITY_BUTTON_SIZE }} onClick={onToggleComparison}>
                            <ComparisonIcon size={SWING_ACTIVITY_BUTTON_SIZE} color={iconColors.comparison} />
                            <span>
                                {comparisonSwingId === uuid
                                    ? t('activity_navigation.item_remove_as_comparison')
                                    : t('activity_navigation.item_mark_as_comparison')}
                            </span>
                        </MenuItem>
                        {onDelete && (
                            <MenuItem style={{ paddingInline: SWING_ACTIVITY_BUTTON_SIZE }} onClick={onDeleteClick}>
                                <TrashIcon size={SWING_ACTIVITY_BUTTON_SIZE} color={iconColors.delete} />
                                <span>{t('activity_navigation.item_delete')}</span>
                            </MenuItem>
                        )}
                    </Menu>
                }
            />

            {isSwingDeleteModalOpen && (
                <SwingDeleteConfirmationModal
                    isDeleting={isDeletingSwing}
                    onAccept={handleDeleteAccept}
                    onDismiss={handleDeleteDismiss}
                />
            )}
        </>
    );
};
